/**
 * useFilePicker — provider-dispatch hook (May 2026 rewrite).
 *
 * Unifies four picker transports behind one ``open()`` Promise:
 *
 *   - **Google Drive** uses Google's new **Picker API for desktop and
 *     mobile apps** (beta, April 2026). The BE mints an authorize URL
 *     with ``trigger_onepick=true``, we open it in the user's system
 *     browser via ``@tauri-apps/plugin-shell``, and the public bridge
 *     page on ``app.ando-ai.com`` 302s the callback into the
 *     ``ando://picker?…`` custom scheme. Tauri's deep-link plugin
 *     surfaces it as a ``picker-callback`` event; the hook then calls
 *     ``/drive/picker/exchange`` to hydrate the picked ids into the
 *     unified ``PickedItem`` shape.
 *
 *     This path replaces the legacy ``apis.google.com/picker`` iframe
 *     which silent-fails inside Tauri's production WKWebView because
 *     ``tauri://localhost`` is not a registrable JavaScript origin in
 *     Google Cloud. The ``drive.file`` scope is preserved (granular
 *     consent → no CASA assessment).
 *
 *   - **OneDrive / SharePoint / Notion** open React modals (Drawer-
 *     based) — Microsoft doesn't ship a v2 file picker that supports
 *     "browse all sites" and Notion has no native picker at all.
 *
 * Caller pattern is unchanged:
 *
 *   const fp = useFilePicker();
 *   const handleAdd = async () => {
 *     const { items } = await fp.open({ provider: "google_drive" });
 *     // ... persist
 *   };
 *   return <>{children}{fp.modalElement}</>;
 *
 * In a browser tab (non-Tauri) the Google branch falls back to opening
 * the authorize URL in a new tab and listens for the bridge page to
 * ``postMessage`` the callback — same shape, just no Tauri shell.
 */

import { toast } from "@ando/ui";
import * as React from "react";
import { useLingui } from "@lingui/react/macro";

import { GraphTreePickerModal, NotionPickerModal } from "@/features/sync/components/picker";
import type {
  PickedItem,
  PickerOpenOptions,
  PickerResult,
} from "@/features/sync/components/picker";
import type { PickerCallbackPayload } from "@/features/sync/hooks/usePickerCallback";

// ──────────────────────────────────────────────────────────────────────
// Types
// ──────────────────────────────────────────────────────────────────────

interface ActiveModal {
  readonly provider: "onedrive" | "sharepoint" | "notion" | "google_drive";
  readonly connectionKey?: string;
  readonly resolve: (result: PickerResult) => void;
}

export interface UseFilePickerResult {
  readonly open: (opts: PickerOpenOptions) => Promise<PickerResult>;
  readonly isOpening: boolean;
  /**
   * JSX surface the host page must render. ``null`` when no modal is
   * active. Mount once at the bottom of the host's tree — e.g.
   * ``<>{children}{filePicker.modalElement}</>``.
   */
  readonly modalElement: React.ReactNode;
}

// ──────────────────────────────────────────────────────────────────────
// Hook
// ──────────────────────────────────────────────────────────────────────

export function useFilePicker(): UseFilePickerResult {
  const { t } = useLingui();
  const [isOpening, setIsOpening] = React.useState(false);
  const [activeModal, setActiveModal] = React.useState<ActiveModal | null>(null);

  const open = React.useCallback((opts: PickerOpenOptions): Promise<PickerResult> => {
    setIsOpening(true);

    switch (opts.provider) {
      case "google_drive":
      case "onedrive":
      case "sharepoint":
      case "notion": {
        // June 11, 2026 — Google Drive migrato dal Picker JS bridge al
        // pattern modal-tree (stesso del OneDrive). Il Picker JS sotto
        // ``drive.file`` autorizzava la folder ma NON i children — non
        // serviva la sync. Adesso che lo scope è ``drive``/``drive.readonly``,
        // ``GraphTreePickerModal`` può chiamare ``folder-tree-cached``
        // BE per Google esattamente come fa per OneDrive. Path unico
        // = manutenzione singola.
        return new Promise<PickerResult>((resolve) => {
          setActiveModal({
            provider: opts.provider as ActiveModal["provider"],
            connectionKey: opts.connectionKey,
            resolve: (r) => {
              resolve(r);
              setIsOpening(false);
              setActiveModal(null);
            },
          });
        });
      }
      default: {
        // ``opts.provider`` is typed ``never`` here — any value reaching this
        // branch is an unresolved/legacy provider. Give the user a recovery
        // path instead of the dev-facing enum dump.
        const exhaustive: never = opts.provider;
        void exhaustive;
        setIsOpening(false);
        toast.error(
          t`Provider non disponibile — riconnetti l'account per gestire i file.`,
        );
        return Promise.resolve({ cancelled: true, items: [] });
      }
    }
  }, [t]);

  // ── Modal mount surface ────────────────────────────────────────────
  const modalElement = React.useMemo<React.ReactNode>(() => {
    if (!activeModal) return null;
    const close = (items: readonly PickedItem[], cancelled: boolean): void => {
      activeModal.resolve({ items, cancelled });
    };
    switch (activeModal.provider) {
      case "google_drive":
      case "onedrive":
      case "sharepoint":
        return (
          <GraphTreePickerModal
            open
            mode={activeModal.provider}
            connectionKey={activeModal.connectionKey}
            onPick={(items) => close(items, false)}
            onCancel={() => close([], true)}
          />
        );
      case "notion":
        return (
          <NotionPickerModal
            open
            connectionKey={activeModal.connectionKey}
            onPick={(items) => close(items, false)}
            onCancel={() => close([], true)}
          />
        );
      default: {
        const _exhaustive: never = activeModal.provider;
        return null as never;
      }
    }
  }, [activeModal]);

  return { open, isOpening, modalElement };
}

// Re-export the canonical types so DocSyncManager can import everything
// from one place.
export type { PickedItem, PickerOpenOptions, PickerResult };
export type { PickerCallbackPayload };
